import { Box, Grid, TextField } from "@material-ui/core";
import { Button } from "components";
import { useFormik } from "formik";
import { usePopup } from "hooks/usePopup";
import { useState, useEffect } from "react";
import sellerService from "services/sellerService";
import { Dialog, KeyboardDatePicker } from "shared";
import AutoCompleteProductBaseFormik from "shared/AutoCompleteProductBaseFormik";
import AutoCompleteSellerFormik from "shared/AutoCompleteSellerFormik";
import * as Yup from "yup";

import { ReplaceConfirmDialog } from "../ReplaceConfirmDialog";

const validationSchema = Yup.object({
  seller: Yup.object().nullable().required("Fornecedor é obrigatório"),
  productBase: Yup.object().nullable().required("Produto Base é obrigatório"),
  certifier: Yup.string().required("Certificadora é obrigatória"),
  name: Yup.string().required("Nome do certificado é obrigatório"),
  issueDate: Yup.date()
    .nullable()
    .required("Data de emissão é obrigatória")
    .typeError("Data de emissão inválida")
    .test("is-valid-date", "Data de emissão inválida", function (value) {
      if (!value) return true;
      // Verifica se é uma instância de Date válida
      if (!(value instanceof Date) || isNaN(value.getTime())) {
        return false;
      }
      // Verifica se a data é realmente válida (não aceita 24/24/2050)
      const day = value.getDate();
      const month = value.getMonth();
      const year = value.getFullYear();
      const reconstructed = new Date(year, month, day);
      return (
        reconstructed.getDate() === day &&
        reconstructed.getMonth() === month &&
        reconstructed.getFullYear() === year
      );
    })
    .test(
      "not-after-expiration",
      "Data de emissão não pode ser posterior à data de validade",
      function (value) {
        const { expirationDate } = this.parent;
        if (!value || !expirationDate) return true;
        if (
          !(expirationDate instanceof Date) ||
          isNaN(expirationDate.getTime())
        )
          return true;
        return value <= expirationDate;
      }
    ),
  expirationDate: Yup.date()
    .nullable()
    .required("Data de validade é obrigatória")
    .typeError("Data de validade inválida")
    .test("is-valid-date", "Data de validade inválida", function (value) {
      if (!value) return true;
      // Verifica se é uma instância de Date válida
      if (!(value instanceof Date) || isNaN(value.getTime())) {
        return false;
      }
      // Verifica se a data é realmente válida (não aceita 24/24/2050)
      const day = value.getDate();
      const month = value.getMonth();
      const year = value.getFullYear();
      const reconstructed = new Date(year, month, day);
      return (
        reconstructed.getDate() === day &&
        reconstructed.getMonth() === month &&
        reconstructed.getFullYear() === year
      );
    })
    .test(
      "after-issue-date",
      "Data de validade deve ser posterior à data de emissão",
      function (value) {
        const { issueDate } = this.parent;
        if (!value || !issueDate) return true;
        if (!(issueDate instanceof Date) || isNaN(issueDate.getTime()))
          return true;
        return value >= issueDate;
      }
    ),
  url: Yup.string().url("URL inválida").required("URL é obrigatória")
});

const formatDateToYYYYMMDD = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Função para parsear data sem problemas de timezone
const parseDate = dateString => {
  if (!dateString) return null;
  // Adiciona horário local para evitar problema de timezone
  // "2025-10-09" vira "2025-10-09T00:00:00" no timezone local
  const [year, month, day] = dateString.split("-");
  return new Date(year, month - 1, day);
};

export const CreateCertificateDialog = ({
  open,
  onClose,
  onSuccess,
  initialCertificate = null
}) => {
  const { addPopup } = usePopup();
  const isEditMode = !!initialCertificate;
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [existingCertificate, setExistingCertificate] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [mainDialogOpen, setMainDialogOpen] = useState(open);

  // Sincroniza mainDialogOpen com a prop open
  useEffect(() => {
    setMainDialogOpen(open);
  }, [open]);

  const formik = useFormik({
    initialValues: initialCertificate
      ? {
          seller: {
            id: initialCertificate.seller,
            name: initialCertificate.seller_name
          },
          productBase: {
            id: initialCertificate.product_base_id,
            name: initialCertificate.product_base_name
          },
          certifier: initialCertificate.certifier,
          name: initialCertificate.name,
          issueDate: parseDate(initialCertificate.issue_date),
          expirationDate: parseDate(initialCertificate.expiration_date),
          url: initialCertificate.url
        }
      : {
          seller: null,
          productBase: null,
          certifier: "",
          name: "",
          issueDate: null,
          expirationDate: null,
          url: ""
        },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      // No modo de edição, apenas atualiza
      if (isEditMode) {
        await handleUpdate(values);
        return;
      }

      // No modo de criação, verifica se já existe certificado
      await handleCreate(values);
    }
  });

  const handleCreate = async values => {
    try {
      // Verificar se já existe certificado para esse seller + productBase
      const response = await sellerService.checkExistingCertificate({
        seller: values.seller.id,
        productBase: values.productBase.id
      });

      // Se existir algum resultado, mostrar dialog de confirmação
      if (response.data.results && response.data.results.length > 0) {
        setExistingCertificate(response.data.results[0]);
        setPendingValues(values);
        setMainDialogOpen(false); // Fecha o dialog principal
        setShowReplaceDialog(true); // Abre o dialog de confirmação
        formik.setSubmitting(false);
        return;
      }

      // Se não existir, criar novo certificado
      await createCertificate(values);
    } catch (error) {
      addPopup({
        type: "error",
        title: "Erro ao verificar certificado existente",
        description:
          error?.response?.data?.message || "Tente novamente mais tarde."
      });
    }
  };

  const createCertificate = async values => {
    try {
      const payload = {
        seller: values.seller.id,
        productBaseId: values.productBase.id,
        productBaseName: values.productBase.name,
        certifier: values.certifier,
        name: values.name,
        issueDate: formatDateToYYYYMMDD(values.issueDate),
        expirationDate: formatDateToYYYYMMDD(values.expirationDate),
        url: values.url
      };

      await sellerService.createCertificate(payload);
      addPopup({
        type: "success",
        title: "Certificado criado com sucesso!",
        description: "O certificado foi adicionado ao sistema."
      });

      formik.resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      addPopup({
        type: "error",
        title: "Erro ao criar certificado",
        description:
          error?.response?.data?.message || "Tente novamente mais tarde."
      });
    }
  };

  const handleUpdate = async values => {
    try {
      const payload = {
        seller: values.seller.id,
        productBaseId: values.productBase.id,
        productBaseName: values.productBase.name,
        certifier: values.certifier,
        name: values.name,
        issueDate: formatDateToYYYYMMDD(values.issueDate),
        expirationDate: formatDateToYYYYMMDD(values.expirationDate),
        url: values.url
      };

      await sellerService.updateCertificate(initialCertificate.id, payload);
      addPopup({
        type: "success",
        title: "Certificado atualizado com sucesso!",
        description: "As alterações foram salvas."
      });

      formik.resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      addPopup({
        type: "error",
        title: "Erro ao atualizar certificado",
        description:
          error?.response?.data?.message || "Tente novamente mais tarde."
      });
    }
  };

  const handleReplaceSuccess = () => {
    setShowReplaceDialog(false);
    formik.resetForm();
    setExistingCertificate(null);
    setPendingValues(null);
    setMainDialogOpen(false);
    onSuccess();
    onClose();
  };

  const handleReplaceCancel = () => {
    setShowReplaceDialog(false);
    setMainDialogOpen(true);
    setExistingCertificate(null);
    setPendingValues(null);
  };

  const handleClose = () => {
    formik.resetForm();
    setShowReplaceDialog(false);
    setMainDialogOpen(false);
    setExistingCertificate(null);
    setPendingValues(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={mainDialogOpen}
        handleClose={handleClose}
        title={isEditMode ? "Editar Certificado" : "Adicionar Novo Certificado"}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box style={{ marginBottom: "1.5rem" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AutoCompleteSellerFormik
                  name="seller"
                  formik={formik}
                  label="Fornecedor *"
                  disabled={isEditMode}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AutoCompleteProductBaseFormik
                  name="productBase"
                  formik={formik}
                  label="Produto Base *"
                  disabled={isEditMode}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="certifier"
                  label="Certificadora *"
                  value={formik.values.certifier}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.certifier || formik.submitCount > 0) &&
                    !!formik.errors.certifier
                  }
                  helperText={
                    (formik.touched.certifier || formik.submitCount > 0) &&
                    formik.errors.certifier
                  }
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="name"
                  label="Nome do Certificado *"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.name || formik.submitCount > 0) &&
                    !!formik.errors.name
                  }
                  helperText={
                    (formik.touched.name || formik.submitCount > 0) &&
                    formik.errors.name
                  }
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  label="Data de Emissão *"
                  fullWidth
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  views={["date"]}
                  maxDate={formik.values.expirationDate}
                  invalidDateMessage="Data inválida"
                  maxDateMessage="Data não pode ser posterior à data de validade"
                  strictCompareDates
                  value={formik.values.issueDate}
                  onChange={value => {
                    if (value && value.toString() === "Invalid Date") {
                      formik.setFieldValue("issueDate", null);
                    } else {
                      formik.setFieldValue("issueDate", value);
                    }
                    formik.setFieldTouched("issueDate", true, false);
                    if (formik.values.expirationDate) {
                      formik.setFieldTouched("expirationDate", true, false);
                    }
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("issueDate", true, true);
                  }}
                  error={
                    (formik.touched.issueDate || formik.submitCount > 0) &&
                    !!formik.errors.issueDate
                  }
                  helperText={
                    (formik.touched.issueDate || formik.submitCount > 0) &&
                    formik.errors.issueDate
                  }
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  label="Data de Validade *"
                  fullWidth
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  views={["date"]}
                  minDate={formik.values.issueDate}
                  invalidDateMessage="Data inválida"
                  minDateMessage="Data não pode ser anterior à data de emissão"
                  strictCompareDates
                  value={formik.values.expirationDate}
                  onChange={value => {
                    if (value && value.toString() === "Invalid Date") {
                      formik.setFieldValue("expirationDate", null);
                    } else {
                      formik.setFieldValue("expirationDate", value);
                    }
                    formik.setFieldTouched("expirationDate", true, false);
                    if (formik.values.issueDate) {
                      formik.setFieldTouched("issueDate", true, false);
                    }
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("expirationDate", true, true);
                  }}
                  error={
                    (formik.touched.expirationDate || formik.submitCount > 0) &&
                    !!formik.errors.expirationDate
                  }
                  helperText={
                    (formik.touched.expirationDate || formik.submitCount > 0) &&
                    formik.errors.expirationDate
                  }
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="url"
                  label="URL do Certificado *"
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.url || formik.submitCount > 0) &&
                    !!formik.errors.url
                  }
                  helperText={
                    (formik.touched.url || formik.submitCount > 0) &&
                    formik.errors.url
                  }
                  disabled={formik.isSubmitting}
                  placeholder="https://exemplo.com/certificado.pdf"
                />
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              color="primary"
              variant="contained"
              isLoading={formik.isSubmitting}
            >
              {isEditMode ? "Atualizar" : "Adicionar"}
            </Button>
          </Box>
        </form>
      </Dialog>

      <ReplaceConfirmDialog
        open={showReplaceDialog}
        onClose={handleReplaceCancel}
        onSuccess={handleReplaceSuccess}
        existingCertificate={existingCertificate}
        newCertificateData={pendingValues}
      />
    </>
  );
};
